import {useState} from 'react'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {api, ApiError} from '../../../services/api'
import {useToast} from '../../../components/Toast'

const resetSchema = Yup.object().shape({
  newPassword: Yup.string()
    .min(8, 'Minimum 8 characters')
    .max(72, 'Maximum 72 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords do not match')
    .required('Please confirm your password'),
})

export function ResetPassword() {
  const toast = useToast()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const [loading, setLoading] = useState(false)

  const formik = useFormik({
    initialValues: {newPassword: '', confirmPassword: ''},
    validationSchema: resetSchema,
    onSubmit: async (values, {setStatus}) => {
      setLoading(true)
      try {
        await api.resetPassword(token, values.newPassword)
        toast.success('Your password has been reset. Please sign in.')
        navigate('/auth/login', {replace: true})
      } catch (error) {
        const message =
          error instanceof ApiError ? error.message : 'Something went wrong. Please try again.'
        setStatus(message)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    },
  })

  const inputStyle = (hasError: boolean | undefined) => ({
    width: '100%',
    padding: '12px 14px 12px 44px',
    border: `1px solid ${hasError ? '#f87171' : '#e5e7eb'}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: '#1e293b',
    outline: 'none',
    boxSizing: 'border-box' as const,
    background: '#f9fafb',
  })

  const lockIcon = (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
      <rect x='3' y='11' width='18' height='11' rx='2' ry='2' />
      <path d='M7 11V7a5 5 0 0 1 10 0v4' />
    </svg>
  )

  return (
    <div
      style={{
        background: '#ffffff',
        borderRadius: '16px',
        padding: 'clamp(28px, 7vw, 48px) clamp(20px, 6vw, 40px)',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 14px 44px rgba(27,27,41,0.1)',
        border: '1px solid #eef0f4',
      }}
    >
      <div style={{textAlign: 'center', marginBottom: '32px'}}>
        <img
          src='/app-icon.png'
          alt='App Icon'
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '16px',
            marginBottom: '20px',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
            objectFit: 'cover',
          }}
        />
        <h1 style={{fontSize: '24px', fontWeight: 700, color: '#1e293b', margin: '0 0 6px'}}>
          Set a new password
        </h1>
        <p style={{fontSize: '14px', color: '#64748b', margin: 0}}>Choose a strong password you’ll remember.</p>
      </div>

      {!token ? (
        <>
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              color: '#dc2626',
              fontSize: '14px',
              lineHeight: 1.5,
            }}
          >
            This reset link is missing or invalid. Please request a new one.
          </div>
          <Link
            to='/auth/forgot-password'
            style={{
              display: 'block',
              textAlign: 'center',
              padding: '13px',
              background: 'linear-gradient(135deg, #039540, #027a34)',
              color: '#ffffff',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              textDecoration: 'none',
              letterSpacing: '0.3px',
            }}
          >
            Request a new link
          </Link>
        </>
      ) : (
        <form onSubmit={formik.handleSubmit} noValidate>
          {formik.status && (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '20px',
                color: '#dc2626',
                fontSize: '14px',
              }}
            >
              {formik.status}
            </div>
          )}

          <div style={{marginBottom: '20px'}}>
            <label style={{display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px'}}>
              New Password
            </label>
            <div style={{position: 'relative'}}>
              <div style={{position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af'}}>
                {lockIcon}
              </div>
              <input
                type='password'
                placeholder='••••••••'
                autoComplete='new-password'
                {...formik.getFieldProps('newPassword')}
                style={inputStyle(formik.touched.newPassword && !!formik.errors.newPassword)}
              />
            </div>
            {formik.touched.newPassword && formik.errors.newPassword && (
              <p style={{color: '#dc2626', fontSize: '12px', margin: '4px 0 0'}}>{formik.errors.newPassword}</p>
            )}
          </div>

          <div style={{marginBottom: '24px'}}>
            <label style={{display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px'}}>
              Confirm Password
            </label>
            <div style={{position: 'relative'}}>
              <div style={{position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af'}}>
                {lockIcon}
              </div>
              <input
                type='password'
                placeholder='••••••••'
                autoComplete='new-password'
                {...formik.getFieldProps('confirmPassword')}
                style={inputStyle(formik.touched.confirmPassword && !!formik.errors.confirmPassword)}
              />
            </div>
            {formik.touched.confirmPassword && formik.errors.confirmPassword && (
              <p style={{color: '#dc2626', fontSize: '12px', margin: '4px 0 0'}}>{formik.errors.confirmPassword}</p>
            )}
          </div>

          <button
            type='submit'
            disabled={loading}
            style={{
              width: '100%',
              padding: '13px',
              background: loading ? '#7ec79a' : 'linear-gradient(135deg, #039540, #027a34)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.2s',
              letterSpacing: '0.3px',
            }}
          >
            {loading ? (
              <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
                <span
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: '#ffffff',
                    borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
                Resetting...
              </span>
            ) : (
              'Reset password'
            )}
          </button>

          <p style={{textAlign: 'center', fontSize: '14px', color: '#64748b', margin: '22px 0 0'}}>
            <Link to='/auth/login' style={{color: '#039540', fontWeight: 600, textDecoration: 'none'}}>
              Back to sign in
            </Link>
          </p>
        </form>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
